import {
  buildFilterCategories,
  fetchStaticFilterData
} from '../../utils/filters/filterOptions'
import { getCategoryFilterValues } from '../../utils/filterUtils'
import FirebaseAPI from '@/api/firebase'

export default {
  namespaced: true,
  state: {
    savedFilters: [],

    // Static filter options that are stored in DBs
    languages: [],
    niches: [],

    // Options of remote categories (see filterOptions.js) seen so far, by category id then filter value. Used to
    // label filter values: options picked in the filter dropdown, and those looked up for saved filters.
    remoteFilterOptions: {}
  },
  getters: {
    getFilterCategories: (state, getters, rootState, rootGetters) => {
      return buildFilterCategories({
        savedFilters: state.savedFilters,
        languages: state.languages,
        niches: state.niches,
        boards: rootState.BoardsModule.boards,
        tags: rootState.TagsModule.tags,
        currentProduct: rootGetters['RouterModule/getCurrentRouteProduct']
      })
    },
    getSavedFilters: state => state.savedFilters,
    getRemoteFilterOption: state => (categoryId, value) => state.remoteFilterOptions[categoryId]?.[value] ?? null
  },
  mutations: {
    SET_SAVED_FILTERS (state, filters) {
      state.savedFilters = filters
    },
    SET_LANGUAGES (state, languages) {
      state.languages = languages
    },
    SET_NICHES (state, niches) {
      state.niches = niches
    },
    CACHE_REMOTE_FILTER_OPTIONS (state, { categoryId, optionsByValue }) {
      state.remoteFilterOptions = {
        ...state.remoteFilterOptions,
        [categoryId]: { ...state.remoteFilterOptions[categoryId], ...optionsByValue }
      }
    }
  },
  actions: {
    async fetchFilterCategories ({ state, commit, dispatch }) {
      await Promise.all([
        dispatch('fetchSavedFilters'),
        (async () => {
          // Initial fetch meant to populate the static options that are stored remotely
          const { languages, niches } = await fetchStaticFilterData()

          commit('SET_LANGUAGES', languages)
          commit('SET_NICHES', niches)
        })()
      ])
    },
    async fetchSavedFilters ({ commit, dispatch }) {
      const filters = await FirebaseAPI.Filters.getSavedFilters()
      commit('SET_SAVED_FILTERS', filters)
      // Not awaited: the saved filters' labels fill in once the lookup finishes
      dispatch('resolveSavedFilterOptions')
    },
    cacheRemoteFilterOptions ({ commit }, { category, options }) {
      const optionsByValue = options.reduce((byValue, option) => {
        byValue[category.valueTransform(option[category.optionValueKey])] = option
        return byValue
      }, {})
      commit('CACHE_REMOTE_FILTER_OPTIONS', { categoryId: category.id, optionsByValue })
    },
    // Looks up the options for a remote category's values that haven't been seen yet
    async resolveRemoteFilterOptions ({ state, dispatch }, { category, values }) {
      const seenOptions = state.remoteFilterOptions[category.id] || {}
      const unseenValues = [...new Set(values)].filter(value => !seenOptions[value])
      if (!unseenValues.length) return

      try {
        const options = await category.remoteOptions.fetchByValues(unseenValues)
        dispatch('cacheRemoteFilterOptions', { category, options })
      } catch (error) {
        console.error(`Error looking up ${category.id} filter options`, error)
      }
    },
    // Saved filters can hold remote category values that were never loaded, so their options are looked up for labels
    async resolveSavedFilterOptions ({ state, dispatch }) {
      const remoteCategories = buildFilterCategories().filter(category => category.remoteOptions)
      await Promise.all(remoteCategories.map(category => dispatch('resolveRemoteFilterOptions', {
        category,
        values: state.savedFilters.flatMap(savedFilter => getCategoryFilterValues(savedFilter.filters, category.id))
      })))
    }
  }
}
